/**
 * F3 · ঠিকানা — `/account/addresses`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame F3 (360 × 680).
 *
 * Frame note: "Landmark advice sits next to the addresses rather than inside the form,
 * where it would be read once and forgotten. Two saved addresses cover most of this
 * market."
 *
 * WIRED. `GET /v1/account/addresses` supplies the title, the badge word, the add row and
 * the landmark tip, plus every card. `lines_bn` is an ARRAY on the wire and is rendered
 * as one: a blob split on a newline in the client is how the landmark ends up on the
 * wrong line.
 *
 * The pencil on each card is icon-only and has no name in docs/02's table of nine, so it
 * is drawn presentational rather than given an invented `aria-label`. That also means it
 * is not yet a control: `PATCH /v1/account/addresses` exists and this frame draws no
 * editor to put behind it. Reported, with the missing name, in
 * `docs/bangla-needed/account-activity.md`.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AddressesResponse } from '@oprappo/contracts';
import { Frame, PlusIcon, SAND_FRAME, ScreenHeader } from '../offering/chrome.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import { fetchAddresses } from './api.js';

/** `M14 4l6 6-9 9H5v-6z`, 18px, 2px, teal — the edit glyph F3 draws on each card. */
function PencilGlyph(): JSX.Element {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
      <path d="M14 4l6 6-9 9H5v-6z" stroke="#0E3A45" strokeWidth={2} strokeLinejoin="round" />
    </svg>
  );
}

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what the route returned and renders it; the container below does the
 * loading. That split is what lets the fidelity tests render this screen from the same
 * `fixtures/api/*.json` file the API's own suite asserts its response against, so the
 * chain the visual gate depends on is checked end to end: drawn frame == fixture == API
 * output == this markup.
 *
 * `null` means "nothing has arrived yet", and the view renders its chrome and no content
 * rather than substituting design data.
 */
export function F3AddressesView({ data }: { data: AddressesResponse | null }): JSX.Element {
  const navigate = useNavigate();

  return (
    <Frame className={SAND_FRAME}>
      <ScreenHeader titleBn={data?.title_bn ?? ''} onBack={() => navigate('/account')} />

      <div className="flex-1 p-[16px] oprappo-scroll">
        <div className="flex flex-col gap-[11px]">
          {(data?.items ?? []).map((a) => (
            <div
              key={a.id}
              className={
                'bg-white p-[14px] ' +
                (a.is_default ? 'border-[1.5px] border-teal' : 'border border-border')
              }
            >
              <div className="flex justify-between items-start gap-[11px]">
                <div className="flex-1">
                  {a.is_default ? (
                    <div className="flex gap-[9px] items-center">
                      <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink">
                        {a.label_bn}
                      </div>
                      {/* The one square, filled badge in flow F: teal ground, white word. */}
                      <div className="bg-teal px-[7px] py-[2px] font-anek font-medium text-[13px] leading-[1.75] text-white">
                        {data?.default_bn ?? ''}
                      </div>
                    </div>
                  ) : (
                    <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink">
                      {a.label_bn}
                    </div>
                  )}
                  <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-body mt-[5px]">
                    {/*
                      * Every line the server sent, joined by the frame's own line break.
                      * The frame draws two; hard-coding `[0]` and `[1]` made a third line
                      * silently invisible and a one-line address render an empty second
                      * row — and the second line is the landmark, which is the one this
                      * screen's own tip says matters most.
                      */}
                    {a.lines_bn.map((line, i) => (
                      <span key={line}>
                        {i === 0 ? null : <br />}
                        {line}
                      </span>
                    ))}
                  </div>
                </div>
                <PencilGlyph />
              </div>
            </div>
          ))}
        </div>

        <div className="border border-dashed border-border-warm bg-sand-faint p-[15px] flex gap-[12px] items-center mt-[14px]">
          <PlusIcon size={20} />
          <button
            type="button"
            className="flex-1 text-left font-anek font-semibold text-[15.5px] leading-[1.75] text-teal bg-transparent border-0 p-0 rounded-none"
          >
            {data?.add_bn ?? ''}
          </button>
        </div>

        <div className="bg-white border border-border p-[14px] mt-[20px]">
          <div className="font-anek font-medium text-[15px] leading-[1.75] text-ink">
            {data?.tip_title_bn ?? ''}
          </div>
          <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-body mt-[4px]">
            {data?.tip_body_bn ?? ''}
          </div>
        </div>
      </div>
    </Frame>
  );
}

export function F3Addresses(): JSX.Element {
  const load = useCallback((signal: AbortSignal) => fetchAddresses(signal), []);
  const { data, error } = useResource(load, 'addresses');

  return (
    <SurfaceStates surface="account" error={error ?? undefined}>
      <F3AddressesView data={data} />
    </SurfaceStates>
  );
}
